import { create } from "zustand";
import type { Session } from "../types/contract";
import { api, ApiError } from "../api";
import { useUiMode } from "./uiModeStore";
import { setAccessToken } from "../lib/authToken";
import {
  createAccountForIdToken,
  deleteCurrentFirebaseUser,
  signOutFirebase,
} from "../lib/firebaseAuth";
import { loginSession, restoredFirebaseSession } from "../lib/firebaseProfile";
import { setFirebaseSession } from "../lib/firebaseSession";
import {
  classifySignupError,
  signupFailureText,
  type SignupFailure,
} from "../lib/signupErrors";
import { useOrgStore } from "./orgStore";
import {
  abandonInFlightRefresh,
  fanoutLogout,
  setSessionLostHandler,
  setUnavailableHandler,
} from "../api/httpGateway";

const REFRESH_KEY = "sm_refresh"; // refresh token in localStorage (Front-End.md §0)
const LOCAL_KEYS = ["sm_ui_mode", "sm_layer_detail", "sm_enhanced_visuals"];

interface AuthState {
  session: Session | null;
  status: "booting" | "idle" | "authenticating" | "authed";
  error: string | null;
  signupFailure: SignupFailure | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (
    email: string,
    password: string,
    joinCode: string | undefined,
    uiMode: "easy" | "advanced",
  ) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  adopt: (session: Session) => void; // adopt a server-issued session (join/leave/create returns a fresh one)
}

function persist(set: (p: Partial<AuthState>) => void, s: Session) {
  setFirebaseSession(false);
  setAccessToken(s.access_token);
  if (s.refresh_token) localStorage.setItem(REFRESH_KEY, s.refresh_token);
  set({ session: s, status: "authed", error: null, signupFailure: null });
}

function persistFirebase(set: (p: Partial<AuthState>) => void, s: Session) {
  setFirebaseSession(true);
  setAccessToken(s.access_token);
  removeKey(REFRESH_KEY);
  set({ session: s, status: "authed", error: null, signupFailure: null });
}

function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Private mode — nothing stored to remove.
  }
}

function clearLocal(message: string | null): void {
  setFirebaseSession(false);
  setAccessToken(null);
  removeKey(REFRESH_KEY);
  for (const key of LOCAL_KEYS) removeKey(key);
  useUiMode.setState({
    mode: "advanced",
    layerDetail: "all",
    serverHasMode: false,
    serverHasDetail: false,
  });
  useAuthStore.setState({
    session: null,
    status: "idle",
    error: message,
    signupFailure: null,
  });
  useOrgStore.getState().reset();
}

async function dropFirebaseUser(): Promise<void> {
  try {
    await signOutFirebase();
  } catch {
    // The API session is the one being kept.
  }
}

// The API answered with its own error envelope, so no account row was written.
// Without that answer (timeout, dropped connection, proxy page, or a failure
// after the account came back) the row may exist, and deleting the Firebase
// user would strand it. Sign out instead; the next sign-in finds the account
// or finishes it.
function apiRefusedSignup(e: unknown): boolean {
  return e instanceof ApiError && typeof e.status === "number";
}

async function discardCreatedFirebaseUser(
  createdNow: boolean,
  e: unknown,
): Promise<void> {
  if (!createdNow || !apiRefusedSignup(e)) {
    await dropFirebaseUser();
    return;
  }
  try {
    await deleteCurrentFirebaseUser();
  } catch {
    await dropFirebaseUser();
  }
}

function recordSignupFailure(
  set: (p: Partial<AuthState>) => void,
  e: unknown,
): void {
  const failure = classifySignupError(e);
  set({
    status: "idle",
    error: signupFailureText(failure),
    signupFailure: failure,
  });
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  status: "booting",
  error: null,
  signupFailure: null,
  async login(email, password) {
    set({ status: "authenticating", error: null, signupFailure: null });
    try {
      const adopted = await loginSession(email, password);
      if (adopted.firebase) persistFirebase(set, adopted.session);
      else persist(set, adopted.session);
    } catch (e) {
      set({ status: "idle", error: (e as Error).message, signupFailure: null });
      throw e;
    }
  },
  async signup(email, password, joinCode, uiMode) {
    set({ status: "authenticating", error: null, signupFailure: null });
    let createdNow = false;
    try {
      const idToken = await createAccountForIdToken(email, password);
      createdNow = true;
      setAccessToken(idToken);
      setFirebaseSession(true);
      const created = await api.signup(joinCode, uiMode);
      persistFirebase(set, {
        access_token: idToken,
        user_id: created.user_id,
        email: created.email ?? email,
        orgs: created.orgs,
        role: created.role,
      });
      // The account now carries it, but `/users/me` has not been read yet, so the store would
      // otherwise sit on its "never chose" default (Advanced) until the next fetch — and the user
      // would watch the mode they just picked not take effect.
      useUiMode.getState().setMode(uiMode);
    } catch (e) {
      await discardCreatedFirebaseUser(createdNow, e);
      setAccessToken(null);
      setFirebaseSession(false);
      recordSignupFailure(set, e);
      throw e;
    }
  },
  async logout() {
    // Bump before the request so an in-flight refresh cannot write tokens back
    // when this call returns, and again after so one that started during it cannot.
    abandonInFlightRefresh();
    try {
      await api.logout();
    } finally {
      abandonInFlightRefresh();
      await dropFirebaseUser();
      clearLocal(null);
      publishLogout();
    }
  },
  // Adopt a session the server re-issued (after join/leave/create the token's orgs changes) — same
  // token/refresh handling as login, without a status flip.
  adopt(session) {
    persist(set, session);
  },
  // A stored refresh token is an old password session. Drop it. Everyone on
  // that session signs out once. A Firebase user the API still knows is restored.
  async bootstrap() {
    removeKey(REFRESH_KEY);
    const firebaseSession = await restoredFirebaseSession();
    if (firebaseSession) {
      persistFirebase(set, firebaseSession);
      return;
    }
    set({ status: "idle" });
  },
}));

// Registered after both modules finish. A direct call here runs while the
// gateway module is still evaluating when this module was imported first.
function markUnreachable(message: string): void {
  const signedIn = useAuthStore.getState().session != null;
  useAuthStore.setState({
    status: signedIn ? useAuthStore.getState().status : "idle",
    error: message,
  });
}

queueMicrotask(() => {
  setSessionLostHandler((message) => {
    const state = useAuthStore.getState();
    // Boot ends only in bootstrap(). A 401 while Firebase is still restoring
    // must not wipe the saved org or flip the screen to sign-in.
    if (state.status === "booting" || state.status === "authenticating") return;
    // Logout already dropped the session. Nothing may paint "session expired"
    // until a later sign-in has a session to lose.
    if (state.session == null) return;
    clearLocal(message);
  });
  setUnavailableHandler(markUnreachable);
});

const LOCK_NAME = "vero-refresh";
const STORAGE_LOCK_KEY = "sm_refresh_lock";
const CHANNEL_NAME = "vero-refresh";
const LOCK_TTL_MS = 10_000;
const PEER_WAIT_MS = 400;
const SLOT_KEY = "__veroRefreshSlot";

type Slot = { access: string; refresh: string };
type Shared = { [SLOT_KEY]?: Slot };

let listening = false;

function readSlot(): Slot | undefined {
  return (globalThis as Shared)[SLOT_KEY];
}
function writeSlot(next: Slot): void {
  (globalThis as Shared)[SLOT_KEY] = next;
}
function channelOk(): boolean {
  const ua = globalThis.navigator?.userAgent ?? "";
  // jsdom loads Node's BroadcastChannel, then rejects its MessageEvent.
  return typeof BroadcastChannel !== "undefined" && !/jsdom/i.test(ua);
}
function openChannel(): BroadcastChannel | null {
  if (!channelOk()) return null;
  try {
    return new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return null;
  }
}
function onPeerMessage(channel: BroadcastChannel, data: unknown): void {
  if (!data || typeof data !== "object") return;
  const msg = data as { type?: string; access?: unknown; refresh?: unknown };
  if (
    msg.type === "slot" &&
    typeof msg.access === "string" &&
    typeof msg.refresh === "string"
  ) {
    writeSlot({ access: msg.access, refresh: msg.refresh });
  }
  const current = readSlot();
  if (msg.type === "logout") {
    fanoutLogout();
    clearLocal(null);
    return;
  }
  if (msg.type === "need" && current)
    channel.postMessage({ type: "slot", ...current });
}
export function publishLogout(): void {
  fanoutLogout();
  const channel = openChannel();
  if (!channel) return;
  channel.postMessage({ type: "logout" });
  channel.close();
}
export function installRefreshListener(): void {
  if (listening) return;
  const channel = openChannel();
  if (!channel) return;
  listening = true;
  channel.onmessage = (ev: MessageEvent) => onPeerMessage(channel, ev.data);
}
export function publishRefresh(access: string, refresh: string): void {
  const next = { access, refresh };
  writeSlot(next);
  const channel = openChannel();
  if (!channel) return;
  channel.postMessage({ type: "slot", ...next });
  channel.close();
}
export function askPeerAccess(refresh: string): Promise<string | null> {
  const known = readSlot();
  if (known?.refresh === refresh) return Promise.resolve(known.access);
  const channel = openChannel();
  if (!channel) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      channel.close();
      resolve(null);
    }, PEER_WAIT_MS);
    channel.onmessage = (ev: MessageEvent) => {
      const data = ev.data as {
        type?: string;
        refresh?: string;
        access?: unknown;
      };
      if (data?.type !== "slot" || data.refresh !== refresh) return;
      if (typeof data.access !== "string") return;
      clearTimeout(timer);
      channel.close();
      resolve(data.access);
    };
    channel.postMessage({ type: "need" });
  });
}
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
function lockIsLive(raw: string | null, now: number): boolean {
  if (!raw) return false;
  try {
    const at = (JSON.parse(raw) as { at?: number }).at;
    return typeof at === "number" && now - at < LOCK_TTL_MS;
  } catch {
    return false;
  }
}
async function claimStorageLock(id: string): Promise<boolean> {
  try {
    if (lockIsLive(localStorage.getItem(STORAGE_LOCK_KEY), Date.now()))
      return false;
    localStorage.setItem(
      STORAGE_LOCK_KEY,
      JSON.stringify({ id, at: Date.now() }),
    );
    // Another tab can overwrite between the write and a same-turn read.
    await delay(25);
    const check = localStorage.getItem(STORAGE_LOCK_KEY);
    return !!check && (JSON.parse(check) as { id?: string }).id === id;
  } catch {
    return false;
  }
}
function releaseStorageLock(id: string): void {
  try {
    const raw = localStorage.getItem(STORAGE_LOCK_KEY);
    if (raw && (JSON.parse(raw) as { id?: string }).id === id)
      localStorage.removeItem(STORAGE_LOCK_KEY);
  } catch {
    return;
  }
}
async function withStorageLock<T>(
  fn: () => Promise<T>,
): Promise<T | undefined> {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const deadline = Date.now() + LOCK_TTL_MS;
  let held = false;
  while (Date.now() < deadline) {
    held = await claimStorageLock(id);
    if (held) break;
    await delay(30);
  }
  if (!held) return undefined;
  try {
    return await fn();
  } finally {
    releaseStorageLock(id);
  }
}
export async function lockRefresh<T>(
  fn: () => Promise<T>,
): Promise<T | undefined> {
  const locks = globalThis.navigator?.locks;
  if (locks && typeof locks.request === "function")
    return locks.request(LOCK_NAME, () => fn());
  return withStorageLock(fn);
}
